import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import {
    InformationCircleIcon,
    MagnifyingGlassIcon,
    FunnelIcon,
    CheckCircleIcon,
    ChevronLeftIcon,
    ChevronRightIcon
} from '@heroicons/react/24/outline';
import Button from '@/components/Button/Button';
import FormInput from '@/components/FormInput/FormInput';
import StaticCombobox from '@/components/shared/StaticCombobox/StaticCombobox';
import { CollaboratorAvatar } from '@/features/Collaborators/Shared/CollaboratorAvatar';
import CollaboratorCombobox from '@/components/shared/CollaboratorCombobox/CollaboratorCombobox';
import { useCollaboratorsPublicStaticQuery, CollaboratorPublic } from '@/features/Collaborators/Data';
import {
    createPerformanceEvaluationCampaign,
    EvaluationCollaborator,
    getPerformanceEvaluationCampaignById,
    getPerformanceEvaluationCollaborators,
    updatePerformanceEvaluationCampaign
} from '../services/evaluationsService';
import { useToast } from '@/hooks/useToast';
import { getEvaluationScales, EvaluationScale } from '@/features/PerformanceCriteria/services/scalesService';
import { getQuestionnaires, getQuestionnaireById, Questionnaire } from '../services/questionnairesService';

interface EvaluationForm {
    title: string;
    startDate: string;
    endDate: string;
    questionnaireId: string;
}

interface CollaboratorRow {
    selected: boolean;
    collaborator: CollaboratorPublic;
    responsibleId: string | null;
    responsibleName: string | null;
    responsibleEmail: string | null;
    responsiblePhotoUrl: string | null;
    clientResponsibleName: string | null;
    clientResponsibleEmail: string | null;
}

export const CreateEvaluationPage: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>(); // Check for ID param
    const isEditMode = !!id;

    const { register, handleSubmit, control, setValue, watch, formState: { errors } } = useForm<EvaluationForm>();
    const [submitting, setSubmitting] = useState(false);
    const toast = useToast();

    // Fetch all collaborators for the list
    const { data: collaborators = [], isLoading } = useCollaboratorsPublicStaticQuery();

    // Questionnaires State
    const [questionnaires, setQuestionnaires] = useState<Questionnaire[]>([]);
    const [selectedQuestionnaire, setSelectedQuestionnaire] = useState<Questionnaire | null>(null);
    const [loadingRules, setLoadingRules] = useState(true);

    // Edit Mode Loading State
    const [loadingCampaign, setLoadingCampaign] = useState(false);

    // Watch questionnaire ID to load its details
    const watchedQuestionnaireId = watch('questionnaireId');

    useEffect(() => {
        const loadInitData = async () => {
            setLoadingRules(true);
            try {
                const [qs] = await Promise.all([getQuestionnaires()]);
                setQuestionnaires(qs);
            } catch (e) {
                toast.error("Erro", "Falha ao carregar questionários.");
            } finally {
                setLoadingRules(false);
            }
        };
        loadInitData();
    }, []);

    // Effect to update selectedQuestionnaire when ID changes
    useEffect(() => {
        if (watchedQuestionnaireId) {
            const found = questionnaires.find(q => q.id === watchedQuestionnaireId);
            if (found) setSelectedQuestionnaire(found);
            // If we need to fetch full details (though getQuestionnaires might return enough)
            // If getQuestionnaires is light, we might need getQuestionnaireById. 
            // Current impl of getQuestionnaires returns full docs.
        } else {
            setSelectedQuestionnaire(null);
        }
    }, [watchedQuestionnaireId, questionnaires]);


    // Local state for the table rows
    const [rows, setRows] = useState<CollaboratorRow[]>([]);
    const [initialLoadDone, setInitialLoadDone] = useState(false);

    // --- SCALABILITY: CLIENT-SIDE FILTERING & PAGINATION ---
    const [searchTerm, setSearchTerm] = useState('');
    const [departmentFilter, setDepartmentFilter] = useState('ALL');
    const [showOnlySelected, setShowOnlySelected] = useState(false);
    const [currentPage, setCurrentPage] = useState(0);
    const ITEMS_PER_PAGE = 50;

    // Derived: Unique Departments
    const allDepartments = useMemo(() => {
        const depts = new Set<string>();
        // Use original collaborators list for departments to show all options even if filtered
        collaborators.forEach(c => {
            if (c.departmentName) depts.add(c.departmentName);
        });
        return Array.from(depts).sort();
    }, [collaborators]);

    // Derived: Filtered Rows
    const filteredRows = useMemo(() => {
        let result = rows;

        // 1. Filter by Search (Name)
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            result = result.filter(r => r.collaborator.name.toLowerCase().includes(term));
        }

        // 2. Filter by Department
        if (departmentFilter !== 'ALL') {
            result = result.filter(r => r.collaborator.departmentName === departmentFilter);
        }

        // 3. Filter by "Show Only Selected"
        if (showOnlySelected) {
            result = result.filter(r => r.selected);
        }

        return result;
    }, [rows, searchTerm, departmentFilter, showOnlySelected]);

    // Derived: Paginated Rows
    const paginatedRows = useMemo(() => {
        const start = currentPage * ITEMS_PER_PAGE;
        return filteredRows.slice(start, start + ITEMS_PER_PAGE);
    }, [filteredRows, currentPage]);

    const totalPages = Math.ceil(filteredRows.length / ITEMS_PER_PAGE);

    // Reset pagination when filters change
    useEffect(() => {
        setCurrentPage(0);
    }, [searchTerm, departmentFilter, showOnlySelected]);

    // Toggle All Filtered
    const handleSelectAllFiltered = (select: boolean) => {
        const idsToUpdate = new Set(filteredRows.map(r => r.collaborator.id));

        setRows(prev => prev.map(r => {
            if (idsToUpdate.has(r.collaborator.id)) {
                return { ...r, selected: select };
            }
            return r;
        }));
    };

    const isAllFilteredSelected = filteredRows.length > 0 && filteredRows.every(r => r.selected);

    // --- END SCALABILITY LOGIC ---

    useEffect(() => {
        const init = async () => {
            if (isLoading) return; // Wait for collaborators public list

            if (rows.length > 0 && !isEditMode) return; // Already initialized for create
            if (initialLoadDone) return; // Already loaded

            if (isEditMode && id) {
                setLoadingCampaign(true);
                try {
                    const [campaign, campaignCollaborators] = await Promise.all([
                        getPerformanceEvaluationCampaignById(id),
                        getPerformanceEvaluationCollaborators(id)
                    ]);

                    if (campaign) {
                        // Pre-fill dates (convert Date to YYYY-MM-DD)
                        if (campaign.startDate) {
                            setValue('startDate', campaign.startDate.toISOString().split('T')[0]);
                        }
                        if (campaign.endDate) {
                            setValue('endDate', campaign.endDate.toISOString().split('T')[0]);
                        }
                        if (campaign.questionnaireId) {
                            setValue('questionnaireId', campaign.questionnaireId);
                            // Also need to set this even if list is loading? 
                            // The watcher will pick it up once questionnaires load.
                        }
                    }

                    // Map existing selections to rows
                    const existingMap = new Map<string, EvaluationCollaborator>();
                    campaignCollaborators.forEach(c => existingMap.set(c.collaboratorId, c));

                    const mergedRows = collaborators
                        .filter(c => c.status !== 'inactive')
                        .map(c => {
                            const existing = existingMap.get(c.id);

                            const currentResponsibleId = existing?.responsibleId || c.responsibleId || null;
                            let currentResponsibleEmail = existing?.responsibleEmail || null;
                            if (currentResponsibleId && !currentResponsibleEmail) {
                                const resp = collaborators.find(col => col.id === currentResponsibleId);
                                if (resp) {
                                    currentResponsibleEmail = resp.email;
                                }
                            }

                            return {
                                selected: !!existing,
                                collaborator: c,
                                responsibleId: currentResponsibleId,
                                responsibleName: existing?.responsibleName || c.responsibleName || null,
                                responsibleEmail: currentResponsibleEmail,
                                responsiblePhotoUrl: existing?.responsiblePhotoUrl
                                    || collaborators.find(col => col.id === currentResponsibleId)?.photoUrl
                                    || null,
                                clientResponsibleName: existing?.clientResponsibleName || c.clientResponsible || '',
                                clientResponsibleEmail: existing?.clientResponsibleEmail || '',
                            };
                        });

                    setRows(mergedRows);
                } catch (e) {
                    console.error(e);
                    toast.error("Erro", "Falha ao carregar dados da avaliação.");
                    navigate('/people/evaluations');
                } finally {
                    setLoadingCampaign(false);
                    setInitialLoadDone(true);
                }
            } else {
                // Create Mode
                const initialRows = collaborators
                    .filter(c => c.status !== 'inactive')
                    .map(c => {
                        let currentResponsibleEmail = null;
                        if (c.responsibleId) {
                            const resp = collaborators.find(col => col.id === c.responsibleId);
                            if (resp) {
                                currentResponsibleEmail = resp.email;
                            }
                        }

                        return {
                            selected: true,
                            collaborator: c,
                            responsibleId: c.responsibleId || null,
                            responsibleName: c.responsibleName || null,
                            responsibleEmail: currentResponsibleEmail,
                            responsiblePhotoUrl: collaborators.find(col => col.id === c.responsibleId)?.photoUrl || null,
                            clientResponsibleName: c.clientResponsible || '',
                            clientResponsibleEmail: '',
                        };
                    });
                setRows(initialRows);
                setInitialLoadDone(true);
            }
        };

        init();
    }, [collaborators, isLoading, isEditMode, id, setValue]);



    // Handlers (Refactored to find by ID since indices change with filtering)
    const handleRowChangeById = (collaboratorId: string, field: keyof CollaboratorRow, value: any) => {
        setRows(prev => prev.map(r =>
            r.collaborator.id === collaboratorId ? { ...r, [field]: value } : r
        ));
    };

    const handleResponsibleChangeById = (collaboratorId: string, responsible: CollaboratorPublic | null) => {
        setRows(prev => prev.map(r =>
            r.collaborator.id === collaboratorId
                ? {
                    ...r,
                    responsibleId: responsible?.id || null,
                    responsibleName: responsible?.name || null,
                    responsibleEmail: responsible?.email || null,
                    responsiblePhotoUrl: responsible?.photoUrl || null,
                }
                : r
        ));
    };

    const onSubmit = async (data: EvaluationForm) => {
        setSubmitting(true);
        try {
            const selectedRows = rows.filter(r => r.selected);
            if (selectedRows.length === 0) {
                toast.error("Erro", "Selecione pelo menos um colaborador.");
                setSubmitting(false);
                return;
            }

            if (!selectedQuestionnaire) {
                toast.error("Erro", "Selecione um questionário.");
                setSubmitting(false);
                return;
            }

            // Need scales to snapshot? For now, we are NOT snapshoting global scales if using questionnaire,
            // OR we snapshot global scales if the questionnaire indiciators reference them.
            // Simplest is to snapshot ALL global scales available at time of creation, just in case.
            // OR, better, we don't snapshot scales globally anymore, we rely on them being valid?
            // No, the requirement was "criteriaSnapshot". If indicators use scale IDs, those scales must exist.
            // If we want immutability, we should snapshot scales too.
            const scalesSnapshot = await getEvaluationScales();


            const evaluationCollaborators: EvaluationCollaborator[] = selectedRows.map(r => ({
                collaboratorId: r.collaborator.id,
                collaboratorName: r.collaborator.name,
                collaboratorEmail: r.collaborator.email,
                collaboratorPhotoUrl: r.collaborator.photoUrl || null,
                responsibleId: r.responsibleId,
                responsibleName: r.responsibleName,
                responsibleEmail: r.responsibleEmail || undefined,
                responsiblePhotoUrl: r.responsiblePhotoUrl || null,
                clientResponsibleName: r.clientResponsibleName,
                clientResponsibleEmail: r.clientResponsibleEmail,
                status: 'pending'
            }));

            if (isEditMode && id) {
                // In edit, we do NOT update questionnaire snapshot usually.
                await updatePerformanceEvaluationCampaign(
                    id,
                    {
                        startDate: new Date(data.startDate),
                        endDate: new Date(data.endDate),
                        // We could allow updating questionnaire if it hasn't started? 
                        // For safety, let's say NO for now on snapshot updates.
                        // But we might want to update the displayed questionnaireId if we allow it.
                    },
                    evaluationCollaborators
                );
                toast.success("Sucesso", "Avaliação atualizada com sucesso.");
            } else {
                await createPerformanceEvaluationCampaign(
                    {
                        startDate: new Date(data.startDate),
                        endDate: new Date(data.endDate),
                        status: 'draft',

                        questionnaireId: selectedQuestionnaire.id,
                        questionnaireTitle: selectedQuestionnaire.title,

                        // Snapshot the criteria from the questionnaire
                        criteriaSnapshot: selectedQuestionnaire.criteria,
                        scalesSnapshot: scalesSnapshot
                    },
                    evaluationCollaborators
                );
                toast.success("Sucesso", "Avaliação criada com sucesso.");
            }

            navigate('/people/evaluations');
        } catch (error) {
            console.error(error);
            toast.error("Erro", `Erro ao ${isEditMode ? 'atualizar' : 'criar'} avaliação.`);
        } finally {
            setSubmitting(false);
        }
    };



    if (isLoading || loadingRules || loadingCampaign) return <div className="p-8">Carregando dados...</div>;

    return (
        <div className="py-6">
            <div className="md:flex md:items-center md:justify-between mb-6">
                <div className="min-w-0 flex-1">
                    <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:truncate sm:text-3xl sm:tracking-tight">
                        {isEditMode ? 'Editar Avaliação' : 'Nova Avaliação de Desempenho'}
                    </h2>
                </div>
                <div className="mt-4 flex md:ml-4 md:mt-0">
                    <Button variant="outline" onClick={() => navigate('/people/evaluations')}>
                        Cancelar
                    </Button>
                    <Button className="ml-3" onClick={handleSubmit(onSubmit)} loading={submitting}>
                        {isEditMode ? 'Salvar Alterações' : 'Criar Avaliação'}
                    </Button>
                </div>
            </div>

            <div className="space-y-6">
                {/* Rules Summary */}
                <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <InformationCircleIcon className="h-5 w-5 text-blue-400" aria-hidden="true" />
                        </div>
                        <div className="ml-3">
                            <p className="text-sm text-blue-700">
                                {isEditMode
                                    ? "Você está editando uma campanha existente. O questionário original foi preservado."
                                    : "Selecione um questionário para definir as perguntas e critérios desta avaliação."
                                }
                            </p>
                        </div>
                    </div>
                </div>

                {/* Main Form */}
                <div className="bg-white shadow sm:rounded-lg p-6">
                    <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">

                        {/* Questionnaire Select */}
                        <div className="sm:col-span-6">
                            <label className="block text-sm font-medium text-gray-700">Questionário</label>
                            <Controller
                                control={control}
                                name="questionnaireId"
                                rules={{ required: true }}
                                render={({ field }) => (
                                    <StaticCombobox
                                        options={questionnaires.map(q => ({ label: `${q.title} (${q.type})`, value: q.id || '' }))}
                                        selectedValue={field.value}
                                        onChange={field.onChange}
                                        disabled={isEditMode}
                                        placeholder="Selecione um questionário..."
                                    />
                                )}
                            />
                            {errors.questionnaireId && <span className="text-xs text-red-500">Selecione um questionário</span>}
                            {selectedQuestionnaire && (
                                <p className="mt-1 text-sm text-gray-500">
                                    {selectedQuestionnaire.description} • {selectedQuestionnaire.criteria.length} grupos de critérios.
                                </p>
                            )}
                        </div>

                        <div className="sm:col-span-3">
                            <FormInput
                                label="Data de Início"
                                type="date"
                                {...register('startDate', { required: true })}
                                error={errors.startDate?.type === 'required' ? 'Campo obrigatório' : undefined}
                            />
                        </div>
                        <div className="sm:col-span-3">
                            <FormInput
                                label="Data Limite"
                                type="date"
                                {...register('endDate', { required: true })}
                                error={errors.endDate?.type === 'required' ? 'Campo obrigatório' : undefined}
                            />
                        </div>
                    </div>
                </div>

                {/* Collaborators List - Unchanged Logic, just re-rendered */}
                <div className="bg-white shadow sm:rounded-lg overflow-hidden flex flex-col">
                    <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
                        <h3 className="text-lg font-medium leading-6 text-gray-900">Colaboradores Incluídos</h3>
                        <p className="mt-1 max-w-2xl text-sm text-gray-500 mb-4">
                            Selecione os colaboradores e defina os responsáveis.
                        </p>

                        {/* --- FILTERS BAR --- */}
                        <div className="flex flex-col md:flex-row md:items-center gap-4 bg-gray-50 p-4 rounded-lg border border-gray-200">
                            {/* Search */}
                            <div className="flex-1 relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    type="text"
                                    className="block w-full pl-10 sm:text-sm border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                                    placeholder="Buscar por nome..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>

                            {/* Department Filter */}
                            <div className="w-full md:w-64 relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <FunnelIcon className="h-5 w-5 text-gray-400" />
                                </div>
                                <select
                                    className="block w-full pl-10 sm:text-sm border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                                    value={departmentFilter}
                                    onChange={(e) => setDepartmentFilter(e.target.value)}
                                >
                                    <option value="ALL">Todos os Departamentos</option>
                                    {allDepartments.map(dept => (
                                        <option key={dept} value={dept}>{dept}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Show Only Selected Toggle */}
                            <button
                                onClick={() => setShowOnlySelected(!showOnlySelected)}
                                className={`flex items-center px-4 py-2 text-sm font-medium rounded-md border ${showOnlySelected
                                    ? 'bg-indigo-50 text-indigo-700 border-indigo-200 ring-1 ring-indigo-200'
                                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                    }`}
                            >
                                {showOnlySelected ? <CheckCircleIcon className="h-5 w-5 mr-2" /> : <div className="h-5 w-5 mr-2 border rounded-full" />}
                                Apenas Selecionados ({rows.filter(r => r.selected).length})
                            </button>
                        </div>
                    </div>

                    <div className="overflow-x-auto min-h-[400px]">
                        <table className="min-w-full divide-y divide-gray-300">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th scope="col" className="relative w-12 px-6 sm:w-16 sm:px-8">
                                        <input
                                            type="checkbox"
                                            className="absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 sm:left-6"
                                            checked={isAllFilteredSelected}
                                            onChange={(e) => handleSelectAllFiltered(e.target.checked)}
                                        />
                                    </th>
                                    <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900">
                                        Colaborador
                                    </th>
                                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 min-w-[250px]">
                                        Responsável Raro
                                    </th>
                                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 min-w-[200px]">
                                        Responsável Cliente (Nome)
                                    </th>
                                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 min-w-[200px]">
                                        Responsável Cliente (Email)
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 bg-white">
                                {paginatedRows.length === 0 ? (
                                    <tr>
                                        <td colSpan={5} className="text-center py-12 text-gray-500">
                                            Nenhum colaborador encontrado com os filtros atuais.
                                        </td>
                                    </tr>
                                ) : (
                                    paginatedRows.map((row) => (
                                        <tr key={row.collaborator.id} className={row.selected ? undefined : 'bg-gray-50 opacity-60'}>
                                            <td className="relative w-12 px-6 sm:w-16 sm:px-8">
                                                <input
                                                    type="checkbox"
                                                    className="absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 sm:left-6"
                                                    checked={row.selected}
                                                    onChange={(e) => handleRowChangeById(row.collaborator.id, 'selected', e.target.checked)}
                                                />
                                            </td>
                                            <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                                <div className="flex items-center">
                                                    <CollaboratorAvatar
                                                        photoUrl={row.collaborator.photoUrl}
                                                        name={row.collaborator.name}
                                                        size="small"
                                                    />
                                                    <div className="ml-4">
                                                        <div className="font-medium text-gray-900">{row.collaborator.name}</div>
                                                        <div className="text-gray-500 text-xs">
                                                            {row.collaborator.positionName} • {row.collaborator.departmentName}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-3 py-4 text-sm text-gray-500">
                                                <CollaboratorCombobox
                                                    selectedId={row.responsibleId}
                                                    onChange={(c) => handleResponsibleChangeById(row.collaborator.id, c)}
                                                    placeholder="Selecione..."
                                                    disabled={!row.selected}
                                                    initialCollaborator={
                                                        row.responsibleId && row.responsibleName
                                                            ? {
                                                                id: row.responsibleId,
                                                                name: row.responsibleName,
                                                                email: row.responsibleEmail || '',
                                                                photoUrl: row.responsiblePhotoUrl || null,
                                                                status: '',
                                                            } as CollaboratorPublic
                                                            : null
                                                    }
                                                    className={row.responsibleId ? '!border-green-500 !ring-green-500' : ''}
                                                />
                                            </td>
                                            <td className="px-3 py-4 text-sm text-gray-500">
                                                <input
                                                    type="text"
                                                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm disabled:bg-gray-100"
                                                    value={row.clientResponsibleName || ''}
                                                    onChange={(e) => handleRowChangeById(row.collaborator.id, 'clientResponsibleName', e.target.value)}
                                                    disabled={!row.selected}
                                                    placeholder="Nome"
                                                />
                                            </td>
                                            <td className="px-3 py-4 text-sm text-gray-500">
                                                <input
                                                    type="email"
                                                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm disabled:bg-gray-100"
                                                    value={row.clientResponsibleEmail || ''}
                                                    onChange={(e) => handleRowChangeById(row.collaborator.id, 'clientResponsibleEmail', e.target.value)}
                                                    disabled={!row.selected}
                                                    placeholder="Email"
                                                />
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* PAGINATION FOOTER */}
                    <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                            <div>
                                <p className="text-sm text-gray-700">
                                    Mostrando <span className="font-medium">{filteredRows.length > 0 ? currentPage * ITEMS_PER_PAGE + 1 : 0}</span> a <span className="font-medium">{Math.min((currentPage + 1) * ITEMS_PER_PAGE, filteredRows.length)}</span> de <span className="font-medium">{filteredRows.length}</span> resultados
                                    {rows.length !== filteredRows.length && (
                                        <span className="text-gray-500 ml-1">(de {rows.length} total)</span>
                                    )}
                                </p>
                            </div>
                            <div>
                                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                                    <button
                                        onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                                        disabled={currentPage === 0}
                                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-300"
                                    >
                                        <span className="sr-only">Anterior</span>
                                        <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                                    </button>
                                    <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                                        Pág {currentPage + 1} de {Math.max(1, totalPages)}
                                    </span>
                                    <button
                                        onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                                        disabled={currentPage >= totalPages - 1}
                                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-300"
                                    >
                                        <span className="sr-only">Próximo</span>
                                        <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                                    </button>
                                </nav>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        </div>
    );
};
